import create from "zustand";

export type ClassroomTileSize = "sm" | "md" | "lg";

/**
 * tileScale lives in [0, 1]:
 *   0 → smallest tile (matches the legacy "sm" preset dimensions)
 *   1 → largest tile  (matches the legacy "lg" preset dimensions)
 * Default of 0.5 is the midpoint, which renders the same dimensions as the
 * legacy "md" default the slider replaced.
 */
const DEFAULT_TILE_SCALE = 0.5;
const TILE_WIDTH_PX_MIN = 150;
const TILE_WIDTH_PX_MAX = 350;
const TILE_CONTENT_HEIGHT_PX_MIN = 120;
const TILE_CONTENT_HEIGHT_PX_MAX = 300;
const TILE_GRID_GAP_PX_MIN = 16;
const TILE_GRID_GAP_PX_MAX = 20;

export interface ClassroomLayoutStore {
	tileScale: number;
	tileSize: ClassroomTileSize;
	// Persisted default-view scale, kept separate from `tileScale` so the
	// seat-plan slider's mirror (SeatCardSizeSlider) can't clobber it.
	defaultTileScale: number;
	setTileScale: (scale: number) => void;
	// Default-view writer: updates both `tileScale` and `defaultTileScale`.
	setDefaultTileScale: (scale: number) => void;
	// Snap `tileScale` back to `defaultTileScale` when leaving seat-plan view.
	restoreDefaultTileScale: () => void;
}

function clamp01(n: number): number {
	if (Number.isNaN(n)) return 0;
	if (n < 0) return 0;
	if (n > 1) return 1;
	return n;
}

function lerp(min: number, max: number, scale: number): number {
	return min + (max - min) * scale;
}

export function tileSizeFromScale(scale: number): ClassroomTileSize {
	const s = clamp01(scale);
	if (s < 0.4) return "sm";
	if (s > 0.8) return "lg";
	return "md";
}

export function tileWidthPxFromScale(scale: number): number {
	return Math.round(lerp(TILE_WIDTH_PX_MIN, TILE_WIDTH_PX_MAX, clamp01(scale)));
}

export function tileContentHeightPxFromScale(scale: number): number {
	return Math.round(lerp(TILE_CONTENT_HEIGHT_PX_MIN, TILE_CONTENT_HEIGHT_PX_MAX, clamp01(scale)));
}

export function tileGridGapPxFromScale(scale: number): number {
	return Math.round(lerp(TILE_GRID_GAP_PX_MIN, TILE_GRID_GAP_PX_MAX, clamp01(scale)));
}

// Below this slider position the tab-favicon row at the bottom of each
// student screenshot is hidden — at very compact sizes those icons don't
// fit cleanly. Intentionally lower than the "sm" bucket threshold (0.4) so
// the icons reappear before the full md layout does.
const SHOW_TAB_ICONS_SCALE_MIN = 0.3;

/**
 * Whether the tab-favicon row on each tile's screenshot should render at the
 * given slider scale. Hidden at the very bottom of the slider because the
 * icons overflow the compact tile; visible everywhere else.
 */
export function showsTabIconsFromScale(scale: number): boolean {
	return clamp01(scale) >= SHOW_TAB_ICONS_SCALE_MIN;
}

/**
 * Per-tile context-tab cap. The slider is split into 5 equal sections:
 *   section 1 (scale <0.2)        → 3 tabs
 *   sections 2 & 3 (0.2 ≤ scale <0.6) → 5 tabs
 *   sections 4 & 5 (scale ≥0.6)   → 7 tabs
 * This preserves the previous preset caps (3 / 5 / 7) while spreading them
 * across the continuous range.
 */
export function tabItemLimitFromScale(scale: number): number {
	const s = clamp01(scale);
	if (s < 0.2) return 3;
	if (s < 0.6) return 5;
	return 7;
}

/**
 * Store for classroom layout settings, such as tile size.
 */
export const useClassroomLayoutStore = create<ClassroomLayoutStore>((set, get) => ({
	tileScale: DEFAULT_TILE_SCALE,
	defaultTileScale: DEFAULT_TILE_SCALE,
	tileSize: tileSizeFromScale(DEFAULT_TILE_SCALE),
	setTileScale: (raw) => {
		const tileScale = clamp01(raw);
		set({ tileScale, tileSize: tileSizeFromScale(tileScale) });
	},
	setDefaultTileScale: (raw) => {
		const next = clamp01(raw);
		set({ defaultTileScale: next, tileScale: next, tileSize: tileSizeFromScale(next) });
	},
	restoreDefaultTileScale: () => {
		const next = get().defaultTileScale;
		set({ tileScale: next, tileSize: tileSizeFromScale(next) });
	},
}));
